import React, { useState } from "react";
import { DeleteFilled } from "@ant-design/icons";
import { Button, Modal, Table, Form, Input, DatePicker, message } from "antd";
import {
  useGetCustomersQuery,
  useAddCustomerMutation,
  useDeleteCustomerMutation,
} from "../../../redux/Service/api";
import { ExclamationCircleFilled } from "@ant-design/icons";
import Heading from "../../../Components/Heading/Heading";
import ServerError from "../../../Components/utils/ServerError";
import dayjs from "dayjs";

interface Customer {
  CustomerID: number;
  CustomerName: string;
  Address: string;
  Phone: string;
  Mobile: string;
  Email: string;
  FirstBalDate: string;
}

const { confirm } = Modal;

const Customer: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const [addCustomer, { isLoading }] = useAddCustomerMutation();
  const [deleteCustomer, { isLoading: delIsLoading }] = useDeleteCustomerMutation();

  const {
    data: customersData,
    isError: isCustomersError,
    isLoading: isCustomersLoading,
  } = useGetCustomersQuery(undefined);

  const customers: Customer[] = customersData?.ResponseObject ?? [];

  const handleAddCustomer = async () => {
    try {
      const values = await form.validateFields();
      const customerPayload = {
        CustomerName: values.CustomerName,
        Address: values.Address,
        Phone: values.Phone,
        Mobile: values.Mobile,
        Email: values.Email,
        FirstBalDate: values.FirstBalDate.format("YYYY-MM-DDTHH:mm:ss"),
      };

      await addCustomer(customerPayload);
      message.success("تمت إضافة العميل بنجاح");
      form.resetFields();
      setOpenModal(false);
    } catch (error) {
      console.error("خطأ أثناء إضافة العميل:", error);
      message.error("فشل في إضافة العميل. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleDeleteCustomer = (id: number) => {
    confirm({
      title: "هل أنت متأكد أنك تريد حذف هذا العميل؟",
      icon: <ExclamationCircleFilled />,
      content: "بالضغط على موافق سيتم حذف العميل بشكل دائم.",
      async onOk() {
        try {
          await deleteCustomer(id);
          message.success("تم حذف العميل بنجاح");
        } catch (error) {
          console.error(error);
          message.error("فشل في حذف العميل.");
        }
      },
    });
  };

  const columns = [
    {
      title: "اسم العميل",
      dataIndex: "CustomerName",
      key: "CustomerName",
    },
    {
      title: "العنوان",
      dataIndex: "Address",
      key: "Address",
    },
    {
      title: "الهاتف",
      dataIndex: "Phone",
      key: "Phone",
    },
    {
      title: "الجوال",
      dataIndex: "Mobile",
      key: "Mobile",
    },
    {
      title: "البريد الإلكتروني",
      dataIndex: "Email",
      key: "Email",
    },
    {
      title: "تاريخ الرصيد الأولي",
      dataIndex: "FirstBalDate",
      key: "FirstBalDate",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_: any, customer: Customer) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<DeleteFilled />}
            danger
            onClick={() => handleDeleteCustomer(customer.CustomerID)}
          />
        </div>
      ),
    },
  ];

  return (
    <section className="p-4 section_padding mt-0 pt-0 relative">
      <Heading heading="العملاء" />

      {isCustomersError && <ServerError />}

      <div className="text-center my-3">
        <Button
          className="w-[150px]"
          type="primary"
          onClick={() => setOpenModal(true)}
        >
          إضافة عميل
        </Button>
      </div>

      <div className="mx-auto">
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="CustomerID"
          loading={isCustomersLoading || delIsLoading}
          pagination={{ pageSize: 5 }}
        />
      </div>

      <Modal
        title="إضافة عميل"
        centered
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          name="add_customer_form"
          onFinish={handleAddCustomer}
        >
          <Form.Item
            label="اسم العميل"
            name="CustomerName"
            rules={[{ required: true, message: "يرجى إدخال اسم العميل" }]}
          >
            <Input size="large" placeholder="أدخل اسم العميل" />
          </Form.Item>

          <Form.Item
            label="العنوان"
            name="Address"
            rules={[{ required: true, message: "يرجى إدخال العنوان" }]}
          >
            <Input size="large" placeholder="أدخل العنوان" />
          </Form.Item>

          <Form.Item label="الهاتف" name="Phone">
            <Input size="large" placeholder="أدخل رقم الهاتف" />
          </Form.Item>

          <Form.Item label="الجوال" name="Mobile">
            <Input size="large" placeholder="أدخل رقم الجوال" />
          </Form.Item>

          <Form.Item
            label="البريد الإلكتروني"
            name="Email"
            rules={[{ type: "email", message: "يرجى إدخال بريد إلكتروني صحيح" }]}
          >
            <Input size="large" placeholder="أدخل البريد الإلكتروني" />
          </Form.Item>

          <Form.Item
            label="تاريخ الرصيد الأولي"
            name="FirstBalDate"
            rules={[{ required: true, message: "يرجى اختيار تاريخ" }]}
          >
            <DatePicker
              size="large"
              className="w-full"
              placeholder="اختر التاريخ"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              className="w-full"
            >
              إضافة
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default Customer;
